'use client';

import { MessagePlugin } from 'tdesign-react';

const CONFERENCE_CHAT_AUTH_API = '/portalWebAPI/conference/chat/auth/info';
const OVERSEA_CONFERENCE_CHAT_AUTH_API =
  '/portalWebAPI/oversea/conference/chat/auth/info';
const CONFERENCE_CHAT_DEFAULT_DENY_TOAST = '该功能当前暂未开放给当前账号';
const OVERSEA_CONFERENCE_CHAT_DEFAULT_DENY_TOAST =
  'This feature is not available for your account.';
const CONFERENCE_CHAT_HOME_PATH = '/case/activity/summit';
const CONFERENCE_CHAT_PATH = '/case/activity/summit/chat';
const CONFERENCE_CHAT_LEGACY_PATH = '/case/activity/conference/chat';
const CONFERENCE_CHAT_PENDING_KEY = 'conference_chat_pending';
const CONFERENCE_CHAT_LOGOUT_KEY = 'conference_chat_logout_started_at';
const CONFERENCE_CHAT_LOGOUT_TTL = 10_000;
const CONFERENCE_CHAT_LOGIN_CODES = new Set([4000, 4001, 4002]);
const CONFERENCE_CHAT_CLICK_DEBOUNCE_MS = 800;

export type ConferenceChatSite = 'zh' | 'en';

export interface ConferenceChatAuthInfo {
  CanAccess?: boolean;
  ChatLocale?: 'zh' | 'en' | string;
  ChatPageURL?: string;
  DenyToast?: string;
  ContactID?: string;
  ContactType?: string;
  GuestDataSource?: string;
}

export interface ConferenceChatAuthOptions {
  authApi?: string;
  site?: ConferenceChatSite;
  targetSearchParams?: Record<string, string | undefined>;
  openInNewTab?: boolean;
}

export interface ConferenceChatAuthResp {
  code?: number;
  msg?: string;
  data?: ConferenceChatAuthInfo;
  loginRequired?: boolean;
}

const conferenceChatAuthInflight = new Map<
  string,
  Promise<ConferenceChatAuthResp>
>();
let conferenceChatEntryLocked = false;

export interface ConferenceChatPendingAction {
  authApi?: string;
  site?: ConferenceChatSite;
  targetSearchParams?: Record<string, string>;
  sourcePath: string;
  createdAt: number;
}

export type ConferenceChatEntryResult =
  | {
      kind: 'ok';
      url: string;
    }
  | {
      kind: 'login';
    }
  | {
      kind: 'denied';
      message: string;
    };

export const normalizeConferenceChatGuestDataSource = (value?: string) => {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  if (
    normalized === 'intl' ||
    normalized === 'oversea' ||
    normalized === 'overseas'
  ) {
    return 'intl';
  }
  if (normalized === 'cn') {
    return 'cn';
  }
  return undefined;
};

const normalizeConferenceChatContactType = (value?: string) => {
  const normalized = value?.trim().toLowerCase();
  return normalized === 'partner' || normalized === 'audience'
    ? normalized
    : 'guest';
};

export const isConferenceChatGuestSelf = (
  auth?: Pick<
    ConferenceChatAuthInfo,
    'ContactID' | 'ContactType' | 'GuestDataSource'
  >,
  target?: {
    contactId?: string;
    guestDataSource?: string;
  }
) => {
  const selfContactID = auth?.ContactID?.trim();
  const targetContactID = target?.contactId?.trim();
  if (!selfContactID || !targetContactID) {
    return false;
  }
  const selfGuestDataSource = normalizeConferenceChatGuestDataSource(
    auth?.GuestDataSource
  );
  const targetGuestDataSource = normalizeConferenceChatGuestDataSource(
    target?.guestDataSource
  );
  if (!selfGuestDataSource || !targetGuestDataSource) {
    return false;
  }
  return (
    normalizeConferenceChatContactType(auth?.ContactType) === 'guest' &&
    selfContactID === targetContactID &&
    selfGuestDataSource === targetGuestDataSource
  );
};

// getConferenceChatAuthApiBySite 统一维护海内外会聊入口鉴权接口，避免各页面重复硬编码。
export const getConferenceChatAuthApiBySite = (site?: ConferenceChatSite) =>
  site === 'en' ? OVERSEA_CONFERENCE_CHAT_AUTH_API : CONFERENCE_CHAT_AUTH_API;

const getCurrentConferenceChatSite = (): ConferenceChatSite | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const host = window.location.host;
  return host.startsWith('en.') || host.startsWith('en-') ? 'en' : undefined;
};

// getConferenceChatHomeURL 生成登录回跳地址；未登录点击会聊先回 S 大会首页，再由首页续调鉴权。
export const getConferenceChatHomeURL = (site?: ConferenceChatSite) => {
  if (typeof window === 'undefined') {
    return CONFERENCE_CHAT_HOME_PATH;
  }
  const { protocol, host, origin } = window.location;
  if (!site) {
    return `${origin}${CONFERENCE_CHAT_HOME_PATH}`;
  }
  if (site === 'en') {
    if (host === 'test.ssv.tencent.com') {
      return `${protocol}//en-test.ssv.tencent.com${CONFERENCE_CHAT_HOME_PATH}`;
    }
    if (host === 'ssv.tencent.com') {
      return `${protocol}//en.ssv.tencent.com${CONFERENCE_CHAT_HOME_PATH}`;
    }
  }
  if (site === 'zh') {
    if (host === 'en-test.ssv.tencent.com') {
      return `${protocol}//test.ssv.tencent.com${CONFERENCE_CHAT_HOME_PATH}`;
    }
    if (host === 'en.ssv.tencent.com') {
      return `${protocol}//ssv.tencent.com${CONFERENCE_CHAT_HOME_PATH}`;
    }
  }
  return `${origin}${CONFERENCE_CHAT_HOME_PATH}`;
};

// 后端只决定会聊路径；站点域名由前端入口和会聊语言共同约束。
const getConferenceChatTargetURL = (
  data?: ConferenceChatAuthInfo,
  site?: ConferenceChatSite
) => {
  const rawPath = data?.ChatPageURL || CONFERENCE_CHAT_PATH;
  if (typeof window === 'undefined') {
    return rawPath === CONFERENCE_CHAT_LEGACY_PATH
      ? CONFERENCE_CHAT_PATH
      : rawPath;
  }
  let sourceURL: URL;
  try {
    sourceURL = new URL(rawPath, window.location.origin);
  } catch {
    sourceURL = new URL(CONFERENCE_CHAT_PATH, window.location.origin);
  }
  if (sourceURL.pathname === CONFERENCE_CHAT_LEGACY_PATH) {
    sourceURL.pathname = CONFERENCE_CHAT_PATH;
  }
  const targetSite =
    site === 'en' || data?.ChatLocale === 'en' ? 'en' : undefined;
  const targetOrigin = new URL(
    getConferenceChatHomeURL(targetSite),
    window.location.origin
  ).origin;
  const targetURL = new URL(
    `${sourceURL.pathname}${sourceURL.search}${sourceURL.hash}`,
    targetOrigin
  );
  return targetURL.origin === window.location.origin
    ? `${targetURL.pathname}${targetURL.search}${targetURL.hash}`
    : targetURL.toString();
};

export const saveConferenceChatPendingAction = (
  action: ConferenceChatPendingAction
) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.setItem(
    CONFERENCE_CHAT_PENDING_KEY,
    JSON.stringify(action)
  );
};

export const loadConferenceChatPendingAction = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const raw = window.sessionStorage.getItem(CONFERENCE_CHAT_PENDING_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    return JSON.parse(raw) as ConferenceChatPendingAction;
  } catch (error) {
    return undefined;
  }
};

export const clearConferenceChatPendingAction = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(CONFERENCE_CHAT_PENDING_KEY);
};

export const setConferenceChatLogoutInProgress = (active: boolean) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (active) {
      window.sessionStorage.setItem(
        CONFERENCE_CHAT_LOGOUT_KEY,
        String(Date.now())
      );
      return;
    }
    window.sessionStorage.removeItem(CONFERENCE_CHAT_LOGOUT_KEY);
  } catch {
    // 存储不可用时继续退出，不能让辅助标记阻断主流程。
  }
};

export const isConferenceChatLogoutInProgress = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const startedAt = Number(
      window.sessionStorage.getItem(CONFERENCE_CHAT_LOGOUT_KEY)
    );
    return startedAt > 0 && Date.now() - startedAt < CONFERENCE_CHAT_LOGOUT_TTL;
  } catch {
    return false;
  }
};

const buildConferenceChatTargetURL = (
  data?: ConferenceChatAuthInfo,
  targetSearchParams?: Record<string, string | undefined>,
  site?: ConferenceChatSite
) => {
  const urlValue = getConferenceChatTargetURL(data, site);
  if (!targetSearchParams || typeof window === 'undefined') {
    return urlValue;
  }
  const entries = Object.entries(targetSearchParams).filter(
    ([, value]) => typeof value === 'string' && value.trim()
  );
  if (!entries.length) {
    return urlValue;
  }
  const isAbsoluteUrl = /^https?:\/\//i.test(urlValue);
  const url = new URL(urlValue, window.location.origin);
  entries.forEach(([key, value]) => {
    const normalizedValue = value?.trim();
    if (normalizedValue) {
      url.searchParams.set(key, normalizedValue);
    }
  });
  if (isAbsoluteUrl || url.origin !== window.location.origin) {
    return url.toString();
  }
  return `${url.pathname}${url.search}${url.hash}`;
};

const isConferenceChatLoginRequiredPayload = (
  payload?: ConferenceChatAuthResp
) =>
  payload?.code !== undefined && CONFERENCE_CHAT_LOGIN_CODES.has(payload.code);

// redirectConferenceChatLogin 保证登录完成后回到 S 大会首页，由首页按 pending action 继续进入会聊。
const buildConferenceChatLoginURL = (site?: ConferenceChatSite) => {
  const langParam = site === 'en' ? '&s_language=en' : '';
  return `/login?s_url=${encodeURIComponent(
    getConferenceChatHomeURL(site)
  )}${langParam}`;
};

const isSafariBrowser = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const userAgent = navigator.userAgent || '';
  const vendor = navigator.vendor || '';
  const isAppleVendor = /Apple/i.test(vendor);
  const hasSafariToken = /Safari/i.test(userAgent);
  // 仅对 Safari 浏览器保留预开空白页逻辑，避免 iOS 第三方浏览器和常见内置浏览器被误判。
  const isExcludedBrowser =
    /(Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|OPiOS|Opera|Firefox|FxiOS|MicroMessenger|wxwork)/i.test(
      userAgent
    );
  return isAppleVendor && hasSafariToken && !isExcludedBrowser;
};

const openConferenceChatBlankTab = () => {
  if (typeof window === 'undefined' || !isSafariBrowser()) {
    return undefined;
  }
  const targetWindow = window.open('', '_blank');
  if (targetWindow) {
    // Safari 要求新标签页在点击同步栈内创建；鉴权完成后再导航空白页。
    targetWindow.opener = null;
  }
  return targetWindow || undefined;
};

const navigateConferenceChatTarget = (
  url: string,
  targetWindow?: Window,
  openInNewTab?: boolean
) => {
  if (targetWindow && !targetWindow.closed) {
    targetWindow.location.href = url;
    targetWindow.focus();
    return;
  }

  if (openInNewTab) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  window.location.href = url;
};

const closeConferenceChatTarget = (targetWindow?: Window) => {
  if (targetWindow && !targetWindow.closed) {
    targetWindow.close();
  }
};

const redirectConferenceChatLogin = (
  authApi: string,
  site?: ConferenceChatSite,
  targetSearchParams?: Record<string, string | undefined>,
  openInNewTab?: boolean,
  targetWindow?: Window
) => {
  if (typeof window === 'undefined') {
    return;
  }
  const normalizedTargetSearchParams = Object.fromEntries(
    Object.entries(targetSearchParams || {}).reduce<Array<[string, string]>>(
      (result, [key, value]) => {
        if (typeof value === 'string' && value.trim()) {
          result.push([key, value.trim()]);
        }
        return result;
      },
      []
    )
  ) as Record<string, string>;
  saveConferenceChatPendingAction({
    authApi,
    site,
    targetSearchParams: Object.keys(normalizedTargetSearchParams).length
      ? normalizedTargetSearchParams
      : undefined,
    sourcePath: window.location.pathname + window.location.search,
    createdAt: Date.now(),
  });
  const loginURL = buildConferenceChatLoginURL(site);
  if (openInNewTab) {
    navigateConferenceChatTarget(loginURL, targetWindow, true);
    return;
  }
  closeConferenceChatTarget(targetWindow);
  window.location.href = loginURL;
};

// fetchConferenceChatAuthInfo 在点击会聊入口时统一实时查询权限，避免权限变更后沿用旧结果。
export const fetchConferenceChatAuthInfo = async (
  authApi = CONFERENCE_CHAT_AUTH_API
): Promise<ConferenceChatAuthResp> => {
  const inflight = conferenceChatAuthInflight.get(authApi);
  if (inflight) {
    return inflight;
  }

  const request: Promise<ConferenceChatAuthResp> = fetch(authApi, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
    },
    body: JSON.stringify({}),
    cache: 'no-store',
  })
    .then(async (response) => {
      const text = await response.text();
      if (
        !response.ok &&
        (response.status === 401 || response.status === 403)
      ) {
        // 对齐已有 ClientApi 行为：HTTP 401/403 代表登录态问题，直接进入登录流程，不透出后端登录提示。
        return {
          code: response.status,
          msg: '',
          loginRequired: true,
        };
      }
      if (text) {
        try {
          const payload = JSON.parse(text) as ConferenceChatAuthResp;
          return {
            ...payload,
            // 有业务 JSON 时按后端登录态错误码判断，避免把无权限 401 误当成未登录。
            loginRequired: isConferenceChatLoginRequiredPayload(payload),
          };
        } catch (error) {
          // 网关或登录态失效时可能返回非 JSON 的 401/403 页面，此时仍应进入登录流程。
          return {
            code: response.status,
            msg: '',
            loginRequired: response.status === 401 || response.status === 403,
          };
        }
      }
      if (!response.ok || !text) {
        // 未登录时后端可能直接返回 401/403 且无业务 JSON，前端不能把 HTTP 技术错误透出给用户。
        return {
          code: response.status,
          msg: '',
          loginRequired: response.status === 401 || response.status === 403,
        };
      }
      return { code: response.status, msg: '' };
    })
    .finally(() => {
      conferenceChatAuthInflight.delete(authApi);
    });

  conferenceChatAuthInflight.set(authApi, request);
  return request;
};

const getConferenceChatDenyToast = (
  payload?: ConferenceChatAuthResp,
  site?: ConferenceChatSite
) => {
  return (
    (payload?.data?.CanAccess === true ? '' : payload?.data?.DenyToast) ||
    payload?.msg ||
    (site === 'en'
      ? OVERSEA_CONFERENCE_CHAT_DEFAULT_DENY_TOAST
      : CONFERENCE_CHAT_DEFAULT_DENY_TOAST)
  );
};

// requestConferenceChatEntry 只做鉴权和结果归一化，便于登录回跳后复用同一套判断。
export const requestConferenceChatEntry = async (
  options: ConferenceChatAuthOptions = {}
): Promise<ConferenceChatEntryResult> => {
  const site = options.site || getCurrentConferenceChatSite();
  const authApi = options.authApi || getConferenceChatAuthApiBySite(site);
  const targetSearchParams = options.targetSearchParams;
  const payload = await fetchConferenceChatAuthInfo(authApi);
  if (payload?.code === 0 && payload?.data?.CanAccess === true) {
    return {
      kind: 'ok',
      url: buildConferenceChatTargetURL(payload.data, targetSearchParams, site),
    };
  }
  if (payload?.loginRequired) {
    return { kind: 'login' };
  }
  return {
    kind: 'denied',
    message: getConferenceChatDenyToast(payload, site),
  };
};

// openConferenceChatEntry 入口始终可见，点击时再鉴权；无权限时按站点语言展示后端文案。
export const openConferenceChatEntry = async (
  options: ConferenceChatAuthOptions = {}
) => {
  if (conferenceChatEntryLocked) {
    return;
  }
  conferenceChatEntryLocked = true;
  const site = options.site || getCurrentConferenceChatSite();
  const authApi = options.authApi || getConferenceChatAuthApiBySite(site);
  const targetSearchParams = options.targetSearchParams;
  const openInNewTab = options.openInNewTab;
  const targetWindow = openConferenceChatBlankTab();
  try {
    const result = await requestConferenceChatEntry({
      authApi,
      site,
      targetSearchParams,
    });
    if (result.kind === 'ok') {
      navigateConferenceChatTarget(result.url, targetWindow, openInNewTab);
      return;
    }
    if (result.kind === 'login') {
      redirectConferenceChatLogin(
        authApi,
        site,
        targetSearchParams,
        openInNewTab,
        targetWindow
      );
      return;
    }
    closeConferenceChatTarget(targetWindow);
    MessagePlugin.warning(result.message);
  } catch (error) {
    closeConferenceChatTarget(targetWindow);
    console.error('[openConferenceChatEntry error]', error);
    MessagePlugin.warning(
      site === 'en'
        ? OVERSEA_CONFERENCE_CHAT_DEFAULT_DENY_TOAST
        : CONFERENCE_CHAT_DEFAULT_DENY_TOAST
    );
  } finally {
    window.setTimeout(() => {
      conferenceChatEntryLocked = false;
    }, CONFERENCE_CHAT_CLICK_DEBOUNCE_MS);
  }
};
